import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getWorkload } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Building, Users, AlertTriangle, CheckCircle, Clock, BarChart3 } from 'lucide-react';

import { useDepartments } from '../hooks/useDepartments';
const MAX_CAPACITY = 10;

const getWorkloadColor = (status) => {
  switch (status) {
    case 'overloaded': return { bg: 'bg-red-500/10 border-red-500/30', text: 'text-red-600 dark:text-red-400', badge: 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/20', bar: 'bg-red-500' };
    case 'balanced': return { bg: 'bg-amber-500/10 border-amber-500/30', text: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20', bar: 'bg-amber-500' };
    case 'light': return { bg: 'bg-emerald-500/10 border-emerald-500/30', text: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20', bar: 'bg-emerald-500' };
    default: return { bg: 'bg-slate-500/10 border-slate-500/30', text: 'text-slate-500', badge: 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border border-slate-500/20', bar: 'bg-slate-400' };
  }
};

const getWorkloadLabel = (status) => {
  switch (status) {
    case 'overloaded': return 'Overloaded';
    case 'balanced': return 'Balanced';
    case 'light': return 'Light';
    default: return 'Underutilized';
  }
};

export const WorkloadPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin } = useAuth();
  const [workload, setWorkload] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentFilter, setDepartmentFilter] = useState('');

  const fetchWorkload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWorkload(departmentFilter || undefined);
      setWorkload(response.data || []);
    } catch (error) {
      console.error('Failed to fetch workload:', error);
      setWorkload([]);
    } finally {
      setLoading(false);
    }
  }, [departmentFilter]);

  useEffect(() => {
    fetchWorkload();
  }, [fetchWorkload]);

  const totalMembers = workload.length;
  const overloaded = workload.filter(w => w.workload_status === 'overloaded').length;
  const totalOverdue = workload.reduce((sum, w) => sum + w.overdue_tasks, 0);
  const totalActive = workload.reduce((sum, w) => sum + w.active_tasks, 0);

  return (
    <div className="space-y-6" data-testid="workload-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Workload</h1>
          <p className="text-muted-foreground mt-1">Monitor team capacity and task distribution</p>
        </div>
        {isSuperAdmin && (
          <Select value={departmentFilter || "all"} onValueChange={(v) => setDepartmentFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="department-filter">
              <Building className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {DEPARTMENTS.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-members">{totalMembers}</p>
                <p className="text-xs text-muted-foreground">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#F5A623]/10">
                <BarChart3 className="h-5 w-5 text-[#F5A623]" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-active">{totalActive}</p>
                <p className="text-xs text-muted-foreground">Active Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-overloaded">{overloaded}</p>
                <p className="text-xs text-muted-foreground">Overloaded</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-overdue">{totalOverdue}</p>
                <p className="text-xs text-muted-foreground">Overdue Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workload Cards */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Team Workload</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
            </div>
          ) : workload.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No team members found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {workload.map((member) => {
                const colors = getWorkloadColor(member.workload_status);
                const capacityPercent = Math.min((member.active_tasks / MAX_CAPACITY) * 100, 100);

                return (
                  <div
                    key={member.user_id}
                    className={`p-4 rounded-xl border-2 transition-colors ${colors.bg}`}
                    data-testid={`workload-member-${member.user_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* User Info */}
                      <div className="flex items-center gap-3 sm:w-1/4">
                        <Avatar className="h-10 w-10 bg-[#F5A623]/20 shrink-0">
                          <AvatarFallback className="text-[#F5A623] font-semibold text-sm">
                            {member.user_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{member.user_name}</p>
                          <p className="text-xs text-muted-foreground">{member.role} - {member.department}</p>
                        </div>
                      </div>

                      {/* Capacity Bar */}
                      <div className="flex-1 space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Capacity ({member.active_tasks}/{MAX_CAPACITY})</span>
                          <Badge className={colors.badge}>{getWorkloadLabel(member.workload_status)}</Badge>
                        </div>
                        <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${colors.bar}`}
                            style={{ width: `${capacityPercent}%` }}
                          />
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-xs sm:w-auto">
                        <div className="text-center">
                          <p className="font-bold text-base">{member.todo_tasks}</p>
                          <p className="text-muted-foreground">To Do</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-base text-blue-500">{member.in_progress_tasks}</p>
                          <p className="text-muted-foreground">Active</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-base text-emerald-500">{member.completed_tasks}</p>
                          <p className="text-muted-foreground">Done</p>
                        </div>
                        {member.overdue_tasks > 0 && (
                          <div className="text-center">
                            <p className="font-bold text-base text-red-500">{member.overdue_tasks}</p>
                            <p className="text-muted-foreground">Overdue</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="font-medium">Workload Status:</span>
        {[
          { label: 'Overloaded', color: 'bg-red-500' },
          { label: 'Balanced', color: 'bg-amber-500' },
          { label: 'Light', color: 'bg-emerald-500' },
          { label: 'Underutilized', color: 'bg-slate-400' },
        ].map(({ label, color }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
